/**
 * D&D 5e built-in character sheet template.
 * Extracted from shared/builtinTemplates.ts for system isolation.
 */

import type { SheetTemplate } from '../../types.js';

export const DND5E_TEMPLATE_ID = 'builtin-dnd5e';

/**
 * action_economy is stored as a JSON-stringified object of the shape:
 *   { action: boolean, bonus: boolean, reaction: boolean }
 * `true` means the slot is still available this turn; `false` means it has been consumed.
 * The default at the start of a turn is JSON.stringify({ action: true, bonus: true, reaction: true }).
 * It is auto-reset by the server's initiative:next handler when the active token changes.
 *
 * movement_spent tracks feet of movement consumed in the current turn; it is reset to 0
 * alongside action_economy at the start of each new turn.
 */
export const DND5E_ACTION_ECONOMY_DEFAULT = JSON.stringify({ action: true, bonus: true, reaction: true });

export const dnd5eTemplate: SheetTemplate = {
  id: DND5E_TEMPLATE_ID,
  name: 'D&D 5e',
  builtIn: true,
  sections: [
    {
      key: 'identity',
      label: 'Основные',
      fields: [
        { key: 'name', label: 'Имя', type: 'text', defaultValue: '' },
        { key: 'race', label: 'Раса', type: 'text', defaultValue: '' },
        { key: 'class_name', label: 'Класс', type: 'text', defaultValue: '' },
        { key: 'level', label: 'Уровень', type: 'number', defaultValue: 1, min: 1, max: 20 },
        { key: 'background', label: 'Предыстория', type: 'text', defaultValue: '' },
        { key: 'alignment', label: 'Мировоззрение', type: 'select', defaultValue: 'N', options: ['LG', 'NG', 'CG', 'LN', 'N', 'CN', 'LE', 'NE', 'CE'] },
        { key: 'xp', label: 'Опыт', type: 'number', defaultValue: 0, min: 0 },
      ],
    },
    {
      key: 'abilities',
      label: 'Характеристики',
      fields: [
        { key: 'str', label: 'Сила', type: 'number', defaultValue: 10, min: 1, max: 30, rollExpression: '1d20 + str_mod' },
        { key: 'dex', label: 'Ловкость', type: 'number', defaultValue: 10, min: 1, max: 30, rollExpression: '1d20 + dex_mod' },
        { key: 'con', label: 'Телосложение', type: 'number', defaultValue: 10, min: 1, max: 30, rollExpression: '1d20 + con_mod' },
        { key: 'int', label: 'Интеллект', type: 'number', defaultValue: 10, min: 1, max: 30, rollExpression: '1d20 + int_mod' },
        { key: 'wis', label: 'Мудрость', type: 'number', defaultValue: 10, min: 1, max: 30, rollExpression: '1d20 + wis_mod' },
        { key: 'cha', label: 'Харизма', type: 'number', defaultValue: 10, min: 1, max: 30, rollExpression: '1d20 + cha_mod' },
        { key: 'str_mod', label: 'Мод. СИЛ', type: 'number', formula: 'floor((str - 10) / 2)' },
        { key: 'dex_mod', label: 'Мод. ЛОВ', type: 'number', formula: 'floor((dex - 10) / 2)' },
        { key: 'con_mod', label: 'Мод. ТЕЛ', type: 'number', formula: 'floor((con - 10) / 2)' },
        { key: 'int_mod', label: 'Мод. ИНТ', type: 'number', formula: 'floor((int - 10) / 2)' },
        { key: 'wis_mod', label: 'Мод. МДР', type: 'number', formula: 'floor((wis - 10) / 2)' },
        { key: 'cha_mod', label: 'Мод. ХАР', type: 'number', formula: 'floor((cha - 10) / 2)' },
        { key: 'prof_bonus', label: 'Бонус мастерства', type: 'number', formula: 'floor((level - 1) / 4) + 2' },
      ],
    },
    {
      key: 'combat',
      label: 'Бой',
      fields: [
        { key: 'ac', label: 'Класс доспеха', type: 'number', defaultValue: 10, min: 0 },
        { key: 'armor_ac', label: 'Бонус доспеха', type: 'number', defaultValue: 0, min: -10, max: 30 },
        { key: 'shield_on', label: 'Щит', type: 'boolean', defaultValue: false },
        { key: 'misc_ac', label: 'Прочий бонус КЗ', type: 'number', defaultValue: 0, min: -20, max: 20 },
        { key: 'hp', label: 'Текущие хиты', type: 'number', defaultValue: 0, min: 0 },
        { key: 'max_hp', label: 'Максимальные хиты', type: 'number', defaultValue: 0, min: 0 },
        { key: 'temp_hp', label: 'Временные хиты', type: 'number', defaultValue: 0, min: 0 },
        { key: 'hit_dice', label: 'Кости хитов', type: 'text', defaultValue: '' },
        { key: 'hit_dice_used', label: 'Использовано КХ', type: 'number', defaultValue: 0, min: 0 },
        { key: 'speed', label: 'Скорость', type: 'number', defaultValue: 30, min: 0 },
        { key: 'initiative_bonus', label: 'Бонус к инициативе', type: 'number', defaultValue: 0, min: -20, max: 20 },
        { key: 'initiative', label: 'Инициатива', type: 'number', formula: 'dex_mod + initiative_bonus' },
        { key: 'death_save_success', label: 'Спасброски (успех)', type: 'number', defaultValue: 0, min: 0, max: 3 },
        { key: 'death_save_fail', label: 'Спасброски (провал)', type: 'number', defaultValue: 0, min: 0, max: 3 },
        { key: 'conditions', label: 'Состояния', type: 'list', defaultValue: [] },
        { key: 'inspiration', label: 'Вдохновение', type: 'boolean', defaultValue: false },
        { key: 'action_economy', label: 'Экономия действий', type: 'text', defaultValue: DND5E_ACTION_ECONOMY_DEFAULT },
        { key: 'movement_spent', label: 'Использовано движения', type: 'number', defaultValue: 0, min: 0 },
        { key: 'combat_notes', label: 'Заметки боя', type: 'text', defaultValue: '' },
      ],
    },
    {
      key: 'saves',
      label: 'Спасброски',
      fields: [
        { key: 'str_save_prof', label: 'Мастерство СИЛ', type: 'number', defaultValue: 0, rollExpression: '1d20 + str_mod' },
        { key: 'dex_save_prof', label: 'Мастерство ЛОВ', type: 'number', defaultValue: 0, rollExpression: '1d20 + dex_mod' },
        { key: 'con_save_prof', label: 'Мастерство ТЕЛ', type: 'number', defaultValue: 0, rollExpression: '1d20 + con_mod' },
        { key: 'int_save_prof', label: 'Мастерство ИНТ', type: 'number', defaultValue: 0, rollExpression: '1d20 + int_mod' },
        { key: 'wis_save_prof', label: 'Мастерство МДР', type: 'number', defaultValue: 0, rollExpression: '1d20 + wis_mod' },
        { key: 'cha_save_prof', label: 'Мастерство ХАР', type: 'number', defaultValue: 0, rollExpression: '1d20 + cha_mod' },
      ],
    },
    {
      key: 'skills',
      label: 'Навыки',
      fields: [
        { key: 'acrobatics_prof', label: 'Акробатика (мастерство)', type: 'number', defaultValue: 0, rollExpression: '1d20 + dex_mod' },
        { key: 'animal_handling_prof', label: 'Уход за животными (мастерство)', type: 'number', defaultValue: 0, rollExpression: '1d20 + wis_mod' },
        { key: 'arcana_prof', label: 'Магия (мастерство)', type: 'number', defaultValue: 0, rollExpression: '1d20 + int_mod' },
        { key: 'athletics_prof', label: 'Атлетика (мастерство)', type: 'number', defaultValue: 0, rollExpression: '1d20 + str_mod' },
        { key: 'deception_prof', label: 'Обман (мастерство)', type: 'number', defaultValue: 0, rollExpression: '1d20 + cha_mod' },
        { key: 'history_prof', label: 'История (мастерство)', type: 'number', defaultValue: 0, rollExpression: '1d20 + int_mod' },
        { key: 'insight_prof', label: 'Проницательность (мастерство)', type: 'number', defaultValue: 0, rollExpression: '1d20 + wis_mod' },
        { key: 'intimidation_prof', label: 'Запугивание (мастерство)', type: 'number', defaultValue: 0, rollExpression: '1d20 + cha_mod' },
        { key: 'investigation_prof', label: 'Расследование (мастерство)', type: 'number', defaultValue: 0, rollExpression: '1d20 + int_mod' },
        { key: 'medicine_prof', label: 'Медицина (мастерство)', type: 'number', defaultValue: 0, rollExpression: '1d20 + wis_mod' },
        { key: 'nature_prof', label: 'Природа (мастерство)', type: 'number', defaultValue: 0, rollExpression: '1d20 + int_mod' },
        { key: 'perception_prof', label: 'Восприятие (мастерство)', type: 'number', defaultValue: 0, rollExpression: '1d20 + wis_mod' },
        { key: 'performance_prof', label: 'Выступление (мастерство)', type: 'number', defaultValue: 0, rollExpression: '1d20 + cha_mod' },
        { key: 'persuasion_prof', label: 'Убеждение (мастерство)', type: 'number', defaultValue: 0, rollExpression: '1d20 + cha_mod' },
        { key: 'religion_prof', label: 'Религия (мастерство)', type: 'number', defaultValue: 0, rollExpression: '1d20 + int_mod' },
        { key: 'sleight_of_hand_prof', label: 'Ловкость рук (мастерство)', type: 'number', defaultValue: 0, rollExpression: '1d20 + dex_mod' },
        { key: 'stealth_prof', label: 'Скрытность (мастерство)', type: 'number', defaultValue: 0, rollExpression: '1d20 + dex_mod' },
        { key: 'survival_prof', label: 'Выживание (мастерство)', type: 'number', defaultValue: 0, rollExpression: '1d20 + wis_mod' },
        { key: 'passive_perception_bonus', label: 'Бонус к пасс. восприятию', type: 'number', defaultValue: 0, min: -20, max: 20 },
        { key: 'passive_perception', label: 'Пассивное восприятие', type: 'number', formula: '10 + wis_mod + passive_perception_bonus' },
      ],
    },
    {
      key: 'notes',
      label: 'Заметки',
      fields: [
        { key: 'personality_traits', label: 'Черты характера', type: 'text', defaultValue: '' },
        { key: 'ideals', label: 'Идеалы', type: 'text', defaultValue: '' },
        { key: 'bonds', label: 'Привязанности', type: 'text', defaultValue: '' },
        { key: 'flaws', label: 'Слабости', type: 'text', defaultValue: '' },
        { key: 'notes', label: 'Заметки', type: 'text', defaultValue: '' },
      ],
    },
    {
      key: 'attacks',
      label: 'Атаки',
      repeating: true,
      fields: [
        { key: 'name', label: 'Название', type: 'text', defaultValue: '' },
        { key: 'to_hit', label: 'Бонус атаки', type: 'number', defaultValue: 0, rollExpression: '1d20 + to_hit' },
        { key: 'damage_dice', label: 'Кости урона', type: 'text', defaultValue: '1d6' },
        { key: 'damage_type', label: 'Тип урона', type: 'select', defaultValue: 'slashing', options: ['slashing', 'piercing', 'bludgeoning', 'fire', 'cold', 'lightning', 'thunder', 'poison', 'acid', 'necrotic', 'radiant', 'force', 'psychic'] },
        { key: 'properties', label: 'Свойства', type: 'text', defaultValue: '' },
      ],
    },
    {
      key: 'inventory',
      label: 'Инвентарь',
      repeating: true,
      fields: [
        { key: 'name', label: 'Название', type: 'text', defaultValue: '' },
        { key: 'quantity', label: 'Количество', type: 'number', defaultValue: 1, min: 0 },
        { key: 'weight', label: 'Вес', type: 'number', defaultValue: 0, min: 0 },
        { key: 'description', label: 'Описание', type: 'text', defaultValue: '' },
        { key: 'equipped', label: 'Экипировано', type: 'boolean', defaultValue: false },
        { key: 'srd_slug', label: 'SRD Slug', type: 'text', defaultValue: '' },
        { key: 'item_type', label: 'Тип предмета', type: 'select', defaultValue: 'gear', options: ['weapon', 'armor', 'shield', 'gear'] },
        { key: 'base_ac', label: 'Базовый КБ', type: 'number', defaultValue: 0, min: 0 },
        { key: 'armor_category', label: 'Категория брони', type: 'select', defaultValue: 'Light', options: ['Light', 'Medium', 'Heavy'] },
        { key: 'attuned', label: 'Привязан', type: 'boolean', defaultValue: false },
      ],
    },
    {
      key: 'spellcasting_info',
      label: 'Заклинания (информация)',
      fields: [
        { key: 'spellcasting_ability', label: 'Характеристика заклинаний', type: 'select', defaultValue: 'none', options: ['none', 'int', 'wis', 'cha'] },
        { key: 'spellcasting_mod', label: 'Мод. заклинаний', type: 'number', defaultValue: 0 },
        { key: 'spell_save_dc', label: 'Spell Save DC', type: 'number', defaultValue: 0 },
        { key: 'spell_attack', label: 'Spell Attack', type: 'number', defaultValue: 0 },
      ],
    },
    {
      key: 'spellSlots',
      label: 'Ячейки заклинаний',
      type: 'spellSlots',
      fields: [],
    },
  ],
  supportsStatBlock: true,
  tokenBinding: {
    hp: 'hp',
    maxHp: 'max_hp',
    ac: 'ac',
    conditions: 'conditions',
  },
};
